import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// PDF for a custom stats report — one table per selected metric, months across.
// Landscape letter, because a 12-month range is a wide table. Light/print theme
// like the QC report: these get printed and forwarded, not read in the app.

const PAGE = { w: 792, h: 612 }; // US Letter landscape, points
const M = 36;

const NAVY = [13, 27, 46];
const GOLD = [201, 168, 76];
const GRAY = [107, 114, 128];

// jsPDF's standard fonts are WinAnsi — client and site names routinely carry
// smart quotes and em dashes that would otherwise render as garbage.
const ascii = (s) =>
  String(s ?? '')
    .replace(/[‘’‛]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[•·]/g, '*')
    .replace(/−/g, '-')
    .replace(/[^\x09\x0A\x0D\x20-\xFF]/g, '');

export const monthLabel = (month) => {
  const [y, m] = String(month).split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const dateLabel = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Counts stay whole; averages and day totals get one decimal.
export const formatValue = (v, agg) => {
  if (v === null || v === undefined) return '';
  return agg === 'count' ? String(Math.round(v)) : (Math.round(v * 10) / 10).toFixed(1);
};

function header(doc, report) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE.w, 62, 'F');

  doc.setTextColor(255, 255, 255).setFont('helvetica', 'bold').setFontSize(15);
  doc.text('Activity Report', M, 30);

  doc.setTextColor(...GOLD).setFont('helvetica', 'bold').setFontSize(9);
  doc.text(ascii(`${dateLabel(report.from)}  -  ${dateLabel(report.to)}`), M, 47);

  doc.setTextColor(255, 255, 255).setFont('helvetica', 'normal').setFontSize(8);
  doc.text('BLVD CAD Portal', PAGE.w - M, 30, { align: 'right' });
  if (report.people?.length) {
    doc.text(ascii(`Filtered to: ${report.people.join(', ')}`), PAGE.w - M, 47, { align: 'right' });
  }
}

function footer(doc, generatedAt) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setTextColor(...GRAY).setFont('helvetica', 'normal').setFontSize(7.5);
    doc.text(ascii(`BLVD CAD Portal - generated ${generatedAt}`), M, PAGE.h - 20);
    doc.text(`Page ${i} of ${pages}`, PAGE.w - M, PAGE.h - 20, { align: 'right' });
  }
}

// `pivoted` is the same shape the on-screen table renders from, so the PDF and
// the page can never disagree: { series, names, colTotal, grand }.
export function buildStatsReportPdf({ report, pivoted, generatedAt }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'landscape' });
  header(doc, report);

  let y = 62 + 24;

  pivoted.forEach((p, i) => {
    const { series, names, colTotal, grand } = p;

    // Keep a section title with at least a couple of rows of its table.
    if (y > PAGE.h - 130 && i > 0) { doc.addPage(); y = M + 12; }

    doc.setTextColor(...NAVY).setFont('helvetica', 'bold').setFontSize(10.5);
    doc.text(ascii(`${series.deptLabel} - ${series.label}`), M, y);
    doc.setTextColor(...GRAY).setFont('helvetica', 'normal').setFontSize(7.5);
    doc.text(ascii(series.note ? `${series.help}  ${series.note}` : series.help || ''), M, y + 11, {
      maxWidth: PAGE.w - M * 2,
    });
    y += 22;

    if (!names.length) {
      doc.setTextColor(...GRAY).setFont('helvetica', 'italic').setFontSize(9);
      doc.text('No activity in this range.', M, y + 4);
      y += 26;
      return;
    }

    const head = [[series.groupLabel, ...report.months.map(monthLabel), 'Total']];
    const body = names.map((r) => [
      ascii(r.name),
      ...report.months.map((mo) => formatValue(r.cells[mo] ?? null, series.agg)),
      formatValue(r.total, series.agg),
    ]);
    body.push([
      series.agg === 'avg' ? 'All (weighted)' : 'All',
      ...report.months.map((mo) => formatValue(colTotal[mo], series.agg)),
      formatValue(grand, series.agg),
    ]);

    const monthCols = {};
    report.months.forEach((_, idx) => { monthCols[idx + 1] = { cellWidth: 'auto', halign: 'center' }; });

    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M, bottom: 40 },
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 4, textColor: NAVY, lineColor: [229, 231, 235], lineWidth: 0.5 },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
      alternateRowStyles: { fillColor: [250, 250, 251] },
      head,
      body,
      columnStyles: {
        0: { cellWidth: 130, fontStyle: 'bold', halign: 'left' },
        ...monthCols,
        [report.months.length + 1]: { cellWidth: 52, halign: 'center', fontStyle: 'bold' },
      },
      // The trailing "All" row is a summary, not another person.
      didParseCell: (d) => {
        if (d.section === 'body' && d.row.index === body.length - 1) {
          d.cell.styles.fillColor = [237, 239, 242];
          d.cell.styles.fontStyle = 'bold';
        }
      },
    });

    y = doc.lastAutoTable.finalY + 22;
  });

  footer(doc, generatedAt);
  return doc;
}

export function downloadStatsReportPdf(args) {
  buildStatsReportPdf(args).save(`blvd-activity-report-${args.report.from}-to-${args.report.to}.pdf`);
}
